import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiArrowRight, HiArrowLeft } from 'react-icons/hi2';
import { HiOutlinePlus, HiOutlineTrash } from 'react-icons/hi';
import { supabase } from '../../utils/supabase';
import { isReservedFieldName } from '../../utils/config';
import { useSchool } from '../../context/SchoolContext';

function SectionBuilder() {
  const navigate = useNavigate();
  const { syncSections } = useSchool();
  const [sections, setSections] = useState([]);
  const [newSectionName, setNewSectionName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const getInitials = (name, existing) => {
    const three = name.slice(0, 3).toUpperCase();
    const clash = existing.find(s => s.initials === three);
    return clash ? name.slice(0, 4).toUpperCase() : three;
  };

  const addSection = () => {
    if (newSectionName.trim() === '') return;
    if (isReservedFieldName(newSectionName)) {
      setError('That name is reserved for system use.');
      return;
    }
    const initials = getInitials(newSectionName, sections);
    setSections([...sections, {
      id: Date.now(),
      name: newSectionName.trim(),
      initials,
      fields: []
    }]);
    setNewSectionName('');
    setError('');
  };

  const deleteSection = (id) => {
    setSections(sections.filter(s => s.id !== id));
  };

  const addField = (sectionId, fieldName) => {
    if (fieldName.trim() === '') return;
    if (isReservedFieldName(fieldName)) {
      setError('Record code, date, time, and ID fields are generated automatically.');
      return;
    }
    setSections(sections.map(s =>
      s.id === sectionId
        ? { ...s, fields: [...s.fields, fieldName.trim()] }
        : s
    ));
    setError('');
  };

  const deleteField = (sectionId, fieldIndex) => {
    setSections(sections.map(s =>
      s.id === sectionId
        ? { ...s, fields: s.fields.filter((_, i) => i !== fieldIndex) }
        : s
    ));
  };

  const handleNext = async () => {
    if (sections.length === 0) return;
    setLoading(true);
    setError('');

    try {
      const schoolId = localStorage.getItem('schoolId');

      if (!schoolId) {
        throw new Error('School account is missing. Please sign in again.');
      }

      const sectionsToInsert = sections.map(s => ({
        school_id: schoolId,
        name: s.name,
        initials: s.initials,
        fields: s.fields
      }));

      const { data, error } = await supabase
        .from('sections')
        .insert(sectionsToInsert)
        .select();

      if (error) throw error;

      localStorage.setItem('sections', JSON.stringify(data));
      syncSections?.(data);
      navigate('/onboarding/complete');

    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen auth-pattern-bg flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-2xl">

        {/* Step Indicator */}
        <div className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">1</div>
          <div className="flex-1 h-1 bg-blue-500 rounded"></div>
          <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold">2</div>
          <div className="flex-1 h-1 bg-gray-200 rounded"></div>
          <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-400 flex items-center justify-center text-sm font-bold">3</div>
        </div>

        {/* Heading */}
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Build Your Sections</h1>
        <p className="text-gray-500 mb-6">Add the sections your school needs and define their fields.</p>

        {/* Add Section Input */}
        <div className="flex gap-2 mb-6">
          <input
            type="text"
            placeholder="e.g. Students, Payroll, Expenses"
            value={newSectionName}
            onChange={(e) => setNewSectionName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addSection()}
            className="flex-1 border border-gray-300 rounded-xl px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={addSection}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-3 rounded-xl flex items-center gap-1 font-semibold transition-all"
          >
            <HiOutlinePlus className="text-xl" />
            Add
          </button>
        </div>

        {/* Sections List */}
        <div className="space-y-4 max-h-80 overflow-y-auto mb-6">
          {sections.length === 0 && (
            <p className="text-center text-gray-400 py-8">No sections added yet. Add one above.</p>
          )}
          {sections.map(section => (
            <SectionCard
              key={section.id}
              section={section}
              onDelete={() => deleteSection(section.id)}
              onAddField={addField}
              onDeleteField={deleteField}
            />
          ))}
        </div>

        {/* Error */}
        {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

        {/* Navigation */}
        <div className="flex justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700 font-semibold transition-all"
          >
            <HiArrowLeft /> Back
          </button>
          <button
            onClick={handleNext}
            disabled={sections.length === 0 || loading}
            className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-200 disabled:text-gray-400 text-white font-semibold py-3 px-6 rounded-xl transition-all flex items-center gap-2"
          >
            {loading ? 'Saving...' : 'Next'}
            {!loading && <HiArrowRight />}
          </button>
        </div>

      </div>
    </div>
  );
}

function SectionCard({ section, onDelete, onAddField, onDeleteField }) {
  const [fieldName, setFieldName] = useState('');

  const handleAdd = () => {
    onAddField(section.id, fieldName);
    setFieldName('');
  };

  return (
    <div className="border border-gray-200 rounded-xl p-4">
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center gap-2">
          <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-1 rounded-lg">
            {section.initials}
          </span>
          <h3 className="font-semibold text-gray-900">{section.name}</h3>
        </div>
        <button onClick={onDelete} className="text-red-400 hover:text-red-600 transition-all">
          <HiOutlineTrash className="text-xl" />
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {section.fields.map((field, index) => (
          <span key={index} className="bg-gray-100 text-gray-700 text-sm px-3 py-1 rounded-lg flex items-center gap-1">
            {field}
            <button onClick={() => onDeleteField(section.id, index)} className="text-gray-400 hover:text-red-500 ml-1">x</button>
          </span>
        ))}
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          placeholder="Add a field e.g. Full Name"
          value={fieldName}
          onChange={(e) => setFieldName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
        <button
          onClick={handleAdd}
          className="bg-gray-100 hover:bg-gray-200 text-gray-600 px-3 py-2 rounded-lg text-sm font-medium transition-all"
        >
          + Field
        </button>
      </div>
    </div>
  );
}

export default SectionBuilder;
